// Goods Received Notes data layer (Phase 4) — header + line items.
//
// The SINGLE interface between the app and the GRN schema
// (supabase/migrations/20260630025804_goods_received_notes.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • We NEVER send company_id or grn_number — DB triggers own them (company
//     forced/inherited; grn_number auto = GRN-00001; both immutable). RLS scopes all.
//
// PURCHASE-ORDER INTEGRATION (the heart of this module):
//   • A PO can have MANY GRNs. After every GRN write we recompute each PO line's
//     received total from ALL non-cancelled GRNs and advance the PO status
//     (sent → partially_received → received), reverting if receipts are removed.
//   • Remaining-per-line is computed; nothing assumes one delivery completes a PO.
//
// RETURN-TYPE CONTRACT (consistent with the other modules):
//   collection → { data, count, page?, pageSize? } · single → T|null ·
//   create/update → id (string) · delete → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { syncGRN as syncInventory, removeGRN as removeInventory } from "./inventory";
import { C } from "../customers/theme";

const GRN = "goods_received_notes";
const LINES = "goods_received_note_lines";
const POL = "purchase_order_lines";
const PO = "purchase_orders";

export const DEFAULT_PAGE_SIZE = 25;

/** Colour-coded delivery statuses (+ DB CHECK parity). */
export const GRN_STATUSES = [
  { value: "pending", label: "Pending", color: C.textMuted },
  { value: "partially_received", label: "Partially Received", color: C.warning },
  { value: "fully_received", label: "Fully Received", color: C.success },
  { value: "rejected", label: "Rejected", color: C.danger },
  { value: "cancelled", label: "Cancelled", color: C.textDim },
];

export const GRN_SORTS = [
  { value: "number", label: "GRN No." },
  { value: "date", label: "Delivery Date" },
  { value: "status", label: "Status" },
  { value: "created", label: "Newest" },
];

const HEADER_WRITE_FIELDS = ["purchase_order_id", "supplier_id", "delivery_date", "delivery_reference", "received_by", "status", "notes"];
const LINE_WRITE_FIELDS = [
  "purchase_order_line_id", "product_id", "sku", "description", "ordered_quantity",
  "previously_received", "quantity_received", "quantity_rejected", "rejection_reason",
  "batch_number", "expiry_date", "unit_cost", "notes",
];
const SORT_COLUMNS = { number: "grn_number", date: "delivery_date", status: "status", created: "created_at" };

const HEADER_SELECT =
  "id, grn_number, status, delivery_date, delivery_reference, received_by, " +
  "purchase_order_id, supplier_id, created_at, updated_at, " +
  "purchase_orders ( po_number ), suppliers ( company_name )";
const FULL_SELECT =
  "*, purchase_orders ( id, po_number, status ), suppliers ( id, company_name, contact_name, email, phone ), " +
  "goods_received_note_lines ( * )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const num = (n) => Number(n) || 0;
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const remainingOf = (l) => Math.max(0, round3(num(l.ordered_quantity) - num(l.previously_received) - num(l.quantity_received)));

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the delivery status."; break;
    case "23503": message = "Referenced purchase order, supplier or product does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = "Goods received note not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List GRNs (RLS-scoped) with search, status/supplier/PO filters, sort, pagination.
 * @param {Object} [opts]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getGRNs(opts = {}) {
  const { search, status, supplierId, purchaseOrderId, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "created", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "created_at";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  let q = c.from(GRN).select(HEADER_SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, from + safeSize - 1);
  if (status && status !== "all") q = q.eq("status", status);
  if (supplierId && supplierId !== "all") q = q.eq("supplier_id", supplierId);
  if (purchaseOrderId && purchaseOrderId !== "all") q = q.eq("purchase_order_id", purchaseOrderId);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`grn_number.ilike.%${term}%,delivery_reference.ilike.%${term}%,received_by.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load goods received notes");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

export async function searchGRNs(term, opts = {}) { return getGRNs({ ...opts, search: term }); }

/** Full GRN (header + PO + supplier + lines, ordered). */
export async function getGRN(id) {
  const { data, error } = await client().from(GRN).select(FULL_SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load goods received note");
  if (data?.goods_received_note_lines) data.goods_received_note_lines.sort((a, b) => (a.position || 0) - (b.position || 0));
  return data || null;
}

/**
 * Purchase orders still open to receive against (status sent / partially_received),
 * with supplier + lines (incl. qty_received) so the form can compute outstanding.
 * @returns {Promise<{data:Array}>}
 */
export async function getReceivablePurchaseOrders() {
  const { data, error } = await client().from(PO)
    .select("id, po_number, status, supplier_id, payment_terms, suppliers ( id, company_name, contact_name, email, phone ), purchase_order_lines ( id, product_id, sku, description, quantity, qty_received, unit_cost, vat_rate )")
    .in("status", ["sent", "partially_received"])
    .order("po_number", { ascending: true });
  if (error) throw toError(error, "Could not load purchase orders");
  return { data: data || [] };
}

// ── Writes ───────────────────────────────────────────────────────────────────

async function replaceLines(grnId, lines) {
  const c = client();
  const { error: delErr } = await c.from(LINES).delete().eq("grn_id", grnId);
  if (delErr) throw toError(delErr, "Could not update GRN lines");
  const rows = (lines || [])
    .filter((l) => l.product_id || (l.description && l.description.trim()) || num(l.quantity_received) > 0 || num(l.quantity_rejected) > 0)
    .map((l, i) => ({ ...pick(l, LINE_WRITE_FIELDS), quantity_remaining: remainingOf(l), position: i, grn_id: grnId }));
  if (rows.length) {
    const { error } = await c.from(LINES).insert(rows);
    if (error) throw toError(error, "Could not save GRN lines");
  }
}

/**
 * Recompute every line's received total on a PO (from all non-cancelled GRNs) and
 * advance the PO status. Idempotent — safe to call after create/update/delete.
 * @param {string} poId
 */
export async function syncPurchaseOrder(poId) {
  if (!poId) return;
  const c = client();
  const { data: polines } = await c.from(POL).select("id, quantity, qty_received").eq("purchase_order_id", poId);
  const { data: grns } = await c.from(GRN).select("status, goods_received_note_lines ( purchase_order_line_id, quantity_received )").eq("purchase_order_id", poId);

  const received = {};
  (grns || []).forEach((g) => {
    if (["cancelled", "rejected"].includes(g.status)) return;
    (g.goods_received_note_lines || []).forEach((l) => {
      if (l.purchase_order_line_id) received[l.purchase_order_line_id] = round3((received[l.purchase_order_line_id] || 0) + num(l.quantity_received));
    });
  });

  for (const pol of polines || []) {
    const got = round3(received[pol.id] || 0);
    if (num(pol.qty_received) !== got) await c.from(POL).update({ qty_received: got }).eq("id", pol.id);
  }

  const lines = (polines || []).map((pol) => ({ qty: num(pol.quantity), got: received[pol.id] || 0 }));
  const anyReceived = lines.some((l) => l.got > 0);
  const allReceived = lines.length > 0 && lines.every((l) => l.qty > 0 && l.got >= l.qty);
  const { data: po } = await c.from(PO).select("status").eq("id", poId).maybeSingle();
  if (po && po.status !== "cancelled" && po.status !== "draft") {
    const next = allReceived ? "received" : anyReceived ? "partially_received" : "sent";
    if (next !== po.status) await c.from(PO).update({ status: next }).eq("id", poId);
  }
}

/**
 * Create a GRN with its received lines, then sync the parent PO.
 * @param {Object} payload  Header fields + `lines: Array`.
 * @returns {Promise<string>} new GRN id.
 */
export async function createGRN(payload) {
  const c = client();
  const { data, error } = await c.from(GRN).insert(pick(payload || {}, HEADER_WRITE_FIELDS)).select("id, purchase_order_id").single();
  if (error) throw toError(error, "Could not create goods received note");
  await replaceLines(data.id, payload?.lines || []);
  await syncPurchaseOrder(data.purchase_order_id);
  await syncInventory(data.id); // received goods flow into the stock ledger
  return data.id;
}

/**
 * Update a GRN header + replace its lines, then re-sync the parent PO.
 * @param {string} id @param {Object} payload  Header fields + `lines: Array`.
 * @returns {Promise<string>} the GRN id.
 */
export async function updateGRN(id, payload) {
  const c = client();
  const { data, error } = await c.from(GRN).update(pick(payload || {}, HEADER_WRITE_FIELDS)).eq("id", id).select("id, purchase_order_id").single();
  if (error) throw toError(error, "Could not update goods received note");
  await replaceLines(id, payload?.lines || []);
  await syncPurchaseOrder(data.purchase_order_id);
  await syncInventory(id); // re-reconcile stock to the edited GRN
  return id;
}

/**
 * Delete a GRN (cascades lines), then re-sync the parent PO so received totals/status
 * roll back correctly.
 * @param {string} id @returns {Promise<void>}
 */
export async function deleteGRN(id) {
  const c = client();
  const { data: grn } = await c.from(GRN).select("purchase_order_id").eq("id", id).maybeSingle();
  const { error } = await c.from(GRN).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete goods received note");
  if (grn?.purchase_order_id) await syncPurchaseOrder(grn.purchase_order_id);
  await removeInventory(id); // roll the received stock back out of the ledger
}
